import type { MedplumClient } from '@medplum/core';
import type { ServiceRequest } from '@medplum/fhirtypes';

import { TRACKER_STAGE_TAG_SYSTEM } from '@unify/fhir-model/constants';
import { requestedTemplateMatchesGroupTemplate } from './requestedGroupTemplate';

export type WaitlistQueueEntry = {
  id: string;
  clientName: string;
  authoredOn?: string;
  patientId?: string;
  serviceRequestId: string;
  serviceRequest: ServiceRequest;
};

/**
 * Load waitlisted Group Therapy ServiceRequests for a Group Template,
 * ordered by authoredOn ascending then Patient id (PRD §8).
 */
export async function searchWaitlistForGroupTemplate(
  medplum: MedplumClient,
  templateReference: string,
): Promise<WaitlistQueueEntry[]> {
  const results = await medplum.searchResources('ServiceRequest', {
    status: 'on-hold',
    '_tag': `${TRACKER_STAGE_TAG_SYSTEM}|waitlist`,
    _count: '100',
    _sort: 'authored',
  });

  const matched = results.filter((sr) =>
    requestedTemplateMatchesGroupTemplate(sr, templateReference),
  );

  matched.sort((a, b) => {
    const aAuth = a.authoredOn ?? '';
    const bAuth = b.authoredOn ?? '';
    if (aAuth !== bAuth) return aAuth.localeCompare(bAuth);
    const aPid = a.subject?.reference?.replace(/^Patient\//, '') ?? '';
    const bPid = b.subject?.reference?.replace(/^Patient\//, '') ?? '';
    return aPid.localeCompare(bPid);
  });

  return matched.map((sr) => ({
    id: sr.id!,
    serviceRequestId: sr.id!,
    serviceRequest: sr,
    authoredOn: sr.authoredOn,
    patientId: sr.subject?.reference?.replace(/^Patient\//, ''),
    clientName: sr.subject?.display ?? sr.subject?.reference ?? 'Unknown client',
  }));
}
